import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import {
    loadJobReadinessDetails,
    loadJobReadinessReasonsDetails
} from '../../../../store/directusService';
import {
    createParticipantJobReadiness,
    updateParticipantJobReadiness
} from '../../../../store/participantService';
import { getConfiguredItems } from '../../../../utils/directusFunctions';
import {
    JOB_READINESS_DETAILS_ERROR,
    JOB_READINESS_REASONS_DETAILS_ERROR
} from '../../../../utils/formValidation/loadingErrorMessageConstants';
import { trimEntries } from '../../../../utils/objectUtils';
import SingleSelect from '../../../formElements/SingleSelect';
import FormActions from '../../../ui/formActions/FormActions';
import LoadingSpinner from '../../../ui/LoadingSpinner';

import { validate } from './validateCreateEditJobReadiness';

import app from '../../../../app.module.css';
import form from '../../../../commonStyles/formStyles.module.css';

const CreateEditJobReadiness = ({ jobReadinessEntry, onCancel }) => {
    // HOOKS
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = { jobReadinessId: '', jobReadinessReasonId: '', ...jobReadinessEntry };
    const [newEntry, setNewEntry] = useState(initialState);
    const [errors, setErrors] = useState({});
    const [jobReadinessArray, setJobReadinessArray] = useState([]);

    // STORE STATE
    const jobReadinessDetails = useSelector(
        (state) => state.entities.directusService.jobReadinessDetails
    );
    const jobReadinessReasonsDetails = useSelector(
        (state) => state.entities.directusService.jobReadinessReasonsDetails
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );

    // USE EFFECTS
    useEffect(() => {
        !jobReadinessDetails?.length && dispatch(loadJobReadinessDetails());
        !jobReadinessReasonsDetails?.length && dispatch(loadJobReadinessReasonsDetails());

        return () => {
            setErrors({});
            setNewEntry(initialState);
        };
    }, []);
    useEffect(() => {
        setNewEntry({ jobReadinessId: '', jobReadinessReasonId: '', ...jobReadinessEntry });
    }, [jobReadinessEntry]);

    useEffect(() => {
        setJobReadinessArray(
            getConfiguredItems(jobReadinessDetails, currentParticipant.contractId)
        );
    }, [jobReadinessDetails, currentParticipant.contractId]);

    useEffect(() => {
        if (
            successMessage === 'Job Readiness created' ||
            successMessage === 'Job Readiness Updated'
        ) {
            onCancel();
        }
    }, [successMessage]);

    // HELPER FNS
    const clearError = (key) => {
        setErrors(trimEntries([key], errors));
    };

    const loadingError = () => {
        if (!jobReadinessDetails?.length || !jobReadinessArray?.length)
            return JOB_READINESS_DETAILS_ERROR;
        if (!jobReadinessReasonsDetails?.length) return JOB_READINESS_REASONS_DETAILS_ERROR;
    };

    // EVENT HANDLERS
    const onUpdate = (key, val) => {
        setNewEntry({ ...newEntry, [key]: val });
        clearError(key);
    };

    const onSubmit = () => {
        const newErrors = validate(newEntry);
        if (Object.keys(newErrors).length) {
            setErrors(newErrors);
            return;
        }

        if (initialState?.id)
            dispatch(
                updateParticipantJobReadiness({ ...newEntry, participantId: currentParticipant.id })
            );
        else
            dispatch(
                createParticipantJobReadiness({ ...newEntry, participantId: currentParticipant.id })
            );
    };

    // RENDER
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    return (
        <div className={form.formWrapper}>
            <h2 className={app.sectionHeading}>
                {initialState.jobReadinessId ? 'Update Job Readiness' : 'Create Job Readiness'}
            </h2>
            <div className={form.formSection}>
                <SingleSelect
                    id="jobReadinessId"
                    label="Job Readiness"
                    placeholder="Select job readiness"
                    mandatory={true}
                    menuItems={jobReadinessArray || []}
                    selectedId={newEntry.jobReadinessId || ''}
                    selected={
                        jobReadinessArray.find((el) => el.id === newEntry.jobReadinessId) || null
                    }
                    error={errors?.jobReadinessId}
                    onChange={(id) => onUpdate('jobReadinessId', id)}
                />
                <SingleSelect
                    id="jobReadinessReasonId"
                    label="Job Readiness Reason"
                    placeholder="Select job readiness reason"
                    menuItems={jobReadinessReasonsDetails || []}
                    selectedId={newEntry.jobReadinessReasonId || ''}
                    selected={
                        jobReadinessReasonsDetails.find(
                            (el) => el.id === newEntry.jobReadinessReasonId
                        ) || null
                    }
                    onChange={(id) => onUpdate('jobReadinessReasonId', id)}
                />
            </div>
            <FormActions
                onClose={onSubmit}
                onCancel={onCancel}
                btnText={`${initialState.jobReadinessId ? 'Update' : 'Add'} Job Readiness`}
            />
        </div>
    );
};

CreateEditJobReadiness.propTypes = {
    jobReadinessEntry: PropTypes.object,
    onCancel: PropTypes.func.isRequired
};

export default CreateEditJobReadiness;
